import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { translateCurrentShellMessage } from '@/features/language/useI18n';
import styles from './NotFoundPage.module.css';

const BEST_SCORE_STORAGE_KEY = 'shell-404-dino-best-score';
const DEFAULT_TRACK_WIDTH = 680;
const GROUND_OFFSET = 28;
const PLAYER_LEFT = 56;
const PLAYER_WIDTH = 44;
const PLAYER_HEIGHT = 48;
const BASE_SPEED = 0.36;
const MAX_SPEED = 0.76;
const SPEED_RAMP = 0.000016;
const GRAVITY = 0.00235;
const JUMP_VELOCITY = 0.82;
const MIN_SPAWN_DELAY = 720;
const MAX_SPAWN_DELAY = 1380;

interface Obstacle {
  id: number;
  x: number;
  width: number;
  height: number;
  branchHeight: number;
  branchWidth: number;
}

interface GameState {
  isRunning: boolean;
  isGameOver: boolean;
  playerY: number;
  velocity: number;
  score: number;
  highScore: number;
  speed: number;
  spawnCooldown: number;
  obstacles: Obstacle[];
  nextObstacleId: number;
  lastTimestamp: number;
}

interface GameView {
  isRunning: boolean;
  isGameOver: boolean;
  playerY: number;
  score: number;
  highScore: number;
  obstacles: Obstacle[];
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const formatScore = (value: number) => clamp(Math.floor(value), 0, 9999).toString().padStart(4, '0');

const getStoredHighScore = () => {
  if (typeof window === 'undefined') {
    return 0;
  }

  const score = Number(window.localStorage.getItem(BEST_SCORE_STORAGE_KEY));
  return Number.isFinite(score) ? Math.max(0, Math.floor(score)) : 0;
};

const persistHighScore = (score: number) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(BEST_SCORE_STORAGE_KEY, String(Math.max(0, Math.floor(score))));
};

const createInitialGame = (highScore: number): GameState => ({
  isRunning: false,
  isGameOver: false,
  playerY: 0,
  velocity: 0,
  score: 0,
  highScore,
  speed: BASE_SPEED,
  spawnCooldown: MAX_SPAWN_DELAY * 0.8,
  obstacles: [],
  nextObstacleId: 1,
  lastTimestamp: 0,
});

const toGameView = (state: GameState): GameView => ({
  isRunning: state.isRunning,
  isGameOver: state.isGameOver,
  playerY: state.playerY,
  score: state.score,
  highScore: state.highScore,
  obstacles: state.obstacles,
});

const pickSpawnDelay = (speed: number) => {
  const speedFactor = (speed - BASE_SPEED) / (MAX_SPEED - BASE_SPEED);
  const variance = Math.random() * 240;

  return clamp(MAX_SPAWN_DELAY - speedFactor * 360 + variance, MIN_SPAWN_DELAY, MAX_SPAWN_DELAY);
};

const createObstacle = (id: number, startX: number): Obstacle => {
  const tall = Math.random() > 0.55;

  return {
    id,
    x: startX + Math.random() * 28,
    width: tall ? 22 : 30,
    height: tall ? 60 + Math.random() * 14 : 42 + Math.random() * 14,
    branchHeight: tall ? 20 + Math.random() * 10 : 14 + Math.random() * 8,
    branchWidth: tall ? 14 : 16,
  };
};

const hasCollision = (obstacles: Obstacle[], playerY: number) => {
  const playerBottom = GROUND_OFFSET + playerY + 4;
  const playerTop = GROUND_OFFSET + playerY + PLAYER_HEIGHT - 2;
  const playerLeft = PLAYER_LEFT + 6;
  const playerRight = PLAYER_LEFT + PLAYER_WIDTH - 6;

  return obstacles.some((obstacle) => {
    const obstacleLeft = obstacle.x + 4;
    const obstacleRight = obstacle.x + obstacle.width - 4;
    const obstacleBottom = GROUND_OFFSET;
    const obstacleTop = GROUND_OFFSET + obstacle.height;

    return (
      playerLeft < obstacleRight &&
      playerRight > obstacleLeft &&
      playerBottom < obstacleTop &&
      playerTop > obstacleBottom
    );
  });
};

export const NotFoundPage = () => {
  const initialHighScoreRef = useRef(getStoredHighScore());
  const gameRef = useRef<GameState>(createInitialGame(initialHighScoreRef.current));
  const frameRef = useRef<number>();
  const stageRef = useRef<HTMLDivElement>(null);
  const trackWidthRef = useRef(DEFAULT_TRACK_WIDTH);
  const primaryActionRef = useRef<() => void>(() => undefined);
  const [game, setGame] = useState<GameView>(() => toGameView(gameRef.current));

  useEffect(() => {
    const syncView = () => {
      setGame(toGameView(gameRef.current));
    };

    const stopLoop = () => {
      if (frameRef.current !== undefined) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = undefined;
      }
    };

    const resetGame = (highScore: number) => {
      stopLoop();
      gameRef.current = createInitialGame(highScore);
      syncView();
    };

    const measureTrack = () => {
      trackWidthRef.current = Math.max(stageRef.current?.clientWidth ?? DEFAULT_TRACK_WIDTH, 320);
    };

    const jump = () => {
      if (gameRef.current.playerY <= 0.5) {
        gameRef.current.velocity = JUMP_VELOCITY;
      }
    };

    const finishRun = () => {
      const state = gameRef.current;
      state.isRunning = false;
      state.isGameOver = true;
      state.velocity = 0;
      state.highScore = Math.max(state.highScore, Math.floor(state.score));
      persistHighScore(state.highScore);
      stopLoop();
    };

    function tick(timestamp: number) {
      const state = gameRef.current;

      if (!state.isRunning) {
        return;
      }

      const elapsed = clamp(timestamp - state.lastTimestamp || 16, 12, 34);
      state.lastTimestamp = timestamp;
      state.score += elapsed * 0.019;
      state.speed = Math.min(MAX_SPEED, state.speed + elapsed * SPEED_RAMP);
      state.spawnCooldown -= elapsed;

      if (state.spawnCooldown <= 0) {
        state.obstacles = [...state.obstacles, createObstacle(state.nextObstacleId, trackWidthRef.current + 32)];
        state.nextObstacleId += 1;
        state.spawnCooldown = pickSpawnDelay(state.speed);
      }

      if (state.playerY > 0 || state.velocity > 0) {
        state.playerY = Math.max(0, state.playerY + state.velocity * elapsed);
        state.velocity -= GRAVITY * elapsed;

        if (state.playerY === 0 && state.velocity < 0) {
          state.velocity = 0;
        }
      }

      state.obstacles = state.obstacles
        .map((obstacle) => ({ ...obstacle, x: obstacle.x - state.speed * elapsed }))
        .filter((obstacle) => obstacle.x + obstacle.width > -40);

      if (hasCollision(state.obstacles, state.playerY)) {
        finishRun();
        syncView();
        return;
      }

      syncView();
      frameRef.current = window.requestAnimationFrame(tick);
    }

    const startRun = () => {
      const state = gameRef.current;

      if (state.isRunning) {
        return;
      }

      state.isRunning = true;
      state.isGameOver = false;
      state.lastTimestamp = performance.now();
      stopLoop();
      frameRef.current = window.requestAnimationFrame(tick);
      syncView();
    };

    const primaryAction = () => {
      if (gameRef.current.isGameOver) {
        const best = Math.max(gameRef.current.highScore, Math.floor(gameRef.current.score));
        resetGame(best);
      }

      if (!gameRef.current.isRunning) {
        startRun();
      }

      jump();
      syncView();
      stageRef.current?.focus({ preventScroll: true });
    };

    primaryActionRef.current = primaryAction;

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;

      if (target?.closest('a, button, input, textarea, select')) {
        return;
      }

      if (event.code === 'Space' || event.code === 'ArrowUp' || event.code === 'KeyW') {
        event.preventDefault();
        primaryAction();
      }
    };

    measureTrack();
    window.addEventListener('resize', measureTrack);
    window.addEventListener('keydown', handleKeyDown);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' && stageRef.current
        ? new ResizeObserver(() => measureTrack())
        : undefined;

    if (stageRef.current && resizeObserver) {
      resizeObserver.observe(stageRef.current);
    }

    stageRef.current?.focus({ preventScroll: true });

    return () => {
      stopLoop();
      resizeObserver?.disconnect();
      window.removeEventListener('resize', measureTrack);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const statusLabel = game.isGameOver
    ? translateCurrentShellMessage('Signal lost')
    : game.isRunning
      ? translateCurrentShellMessage('Running')
      : translateCurrentShellMessage('Ready');

  const bestScore = Math.max(game.highScore, Math.floor(game.score));

  return (
    <section className={`page-shell ${styles.page}`}>
      <div className={styles.hero}>
        <div className={styles.copyCard}>
          <span className="pill">{translateCurrentShellMessage('Route recovery')}</span>
          <h1 className="page-title">404</h1>
          <p className="page-subtitle">
            {translateCurrentShellMessage('The page you are looking for does not exist in this workspace.')}
          </p>
          <p className={styles.instructions}>
            {translateCurrentShellMessage('Press Space, Arrow Up, or tap the track to jump the rover.')}
          </p>
          <div className={styles.actions}>
            <Link to="/" className="btn">
              {translateCurrentShellMessage('Return Home')}
            </Link>
            <span className={styles.supportText}>
              {translateCurrentShellMessage('Best score is stored locally in this browser.')}
            </span>
          </div>
        </div>

        <div className={styles.metricRail}>
          <article className={styles.metricCard}>
            <span className={styles.metricLabel}>{translateCurrentShellMessage('Score')}</span>
            <strong className={styles.metricValue}>{formatScore(game.score)}</strong>
          </article>
          <article className={styles.metricCard}>
            <span className={styles.metricLabel}>{translateCurrentShellMessage('Best')}</span>
            <strong className={styles.metricValue}>{formatScore(bestScore)}</strong>
          </article>
          <article className={styles.metricCard}>
            <span className={styles.metricLabel}>{translateCurrentShellMessage('Status')}</span>
            <strong className={styles.metricValue}>{statusLabel}</strong>
          </article>
        </div>
      </div>

      <div className={styles.gameCard}>
        <div className={styles.gameHeader}>
          <div>
            <h2 className={styles.gameTitle}>{translateCurrentShellMessage('Desert Recovery Run')}</h2>
            <p className={styles.gameLead}>
              {translateCurrentShellMessage('A chrome-dino style fallback while you find your route back into the portal.')}
            </p>
          </div>
          <span className="pill">{statusLabel}</span>
        </div>

        <div
          ref={stageRef}
          className={styles.stage}
          tabIndex={0}
          onPointerDown={(event) => {
            if (event.button !== 0) {
              return;
            }

            const target = event.target as HTMLElement;

            if (target.closest('a, button')) {
              return;
            }

            event.currentTarget.focus({ preventScroll: true });
            primaryActionRef.current();
          }}
          aria-label={translateCurrentShellMessage('404 dino runner game area')}
        >
          <div className={styles.skyline} aria-hidden="true">
            <span className={styles.sun} />
            <span className={`${styles.cloud} ${styles.cloudOne}`} />
            <span className={`${styles.cloud} ${styles.cloudTwo}`} />
            <span className={`${styles.cloud} ${styles.cloudThree}`} />
          </div>

          <div className={styles.scoreBadge} aria-hidden="true">
            {formatScore(game.score)}
          </div>

          <div
            className={styles.player}
            style={{ bottom: `${GROUND_OFFSET + game.playerY}px` }}
            data-running={game.isRunning}
            aria-hidden="true"
          >
            <span className={styles.playerHead} />
            <span className={styles.playerBody} />
            <span className={styles.playerEye} />
            <span className={styles.playerArm} />
            <span className={styles.playerTail} />
            <span className={`${styles.playerLeg} ${styles.playerLegFront}`} />
            <span className={`${styles.playerLeg} ${styles.playerLegRear}`} />
          </div>

          {game.obstacles.map((obstacle) => (
            <div
              key={obstacle.id}
              className={styles.obstacle}
              style={
                {
                  left: `${obstacle.x}px`,
                  width: `${obstacle.width}px`,
                  height: `${obstacle.height}px`,
                  '--branch-height': `${obstacle.branchHeight}px`,
                  '--branch-width': `${obstacle.branchWidth}px`,
                } as CSSProperties
              }
              aria-hidden="true"
            >
              <span className={styles.obstacleBranch} />
              <span className={`${styles.obstacleBranch} ${styles.obstacleBranchRear}`} />
            </div>
          ))}

          <div className={styles.ground} aria-hidden="true">
            <span className={styles.groundLine} />
            <span className={styles.groundDust} />
          </div>

          <div className={styles.overlay} data-visible={!game.isRunning}>
            <p className={styles.overlayEyebrow}>
              {game.isGameOver
                ? translateCurrentShellMessage('Obstacle hit')
                : translateCurrentShellMessage('Route signal interrupted')}
            </p>
            <h2 className={styles.overlayTitle}>
              {game.isGameOver
                ? translateCurrentShellMessage('Run ended. Try another pass.')
                : translateCurrentShellMessage('Run the dino while you get back on course.')}
            </h2>
            <p className={styles.overlayText}>
              {game.isGameOver
                ? translateCurrentShellMessage('Press Space, Arrow Up, or Restart Run to jump back in.')
                : translateCurrentShellMessage('Space, Arrow Up, click, and tap all trigger the jump.')}
            </p>
            <button
              type="button"
              className="btn"
              onClick={(event) => {
                event.stopPropagation();
                primaryActionRef.current();
              }}
            >
              {game.isGameOver
                ? translateCurrentShellMessage('Restart Run')
                : translateCurrentShellMessage('Start Run')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NotFoundPage;
